import type { ReactNode } from "react";
import type { StyleProp, ViewStyle } from "react-native";
import { StyleSheet, View } from "react-native";

import { useCustomTheme } from "../hooks/useCustomTheme";
import { Icon } from "./Icon";
import type { IconSet } from "./Icon";
import { ThemedText } from "./ThemedText";

type ThemedTitleProps = {
  title: string;
  subtitle: string;
  rightButton?: ReactNode;
  leftButton?: ReactNode;
  titleTextContainerStyle?: StyleProp<ViewStyle>;
  contentStyle?: StyleProp<ViewStyle>;
} & (
  | {
      icon: IconSet;
      customIcon?: undefined;
    }
  | {
      icon?: undefined;
      customIcon: ReactNode;
    }
);

export const ThemedTitle = ({
  title,
  subtitle,
  icon,
  customIcon,
  rightButton,
  leftButton,
  titleTextContainerStyle,
  contentStyle,
}: ThemedTitleProps) => {
  const { colors, fonts } = useCustomTheme();
  const renderedIcon =
    customIcon ??
    (icon ? <Icon {...icon} size={24} color={colors.background} /> : null);
  return (
    <View style={[styles.container, { backgroundColor: colors.notification }]}>
      <View style={[styles.content, contentStyle]}>
        <View style={styles.titleContainer}>
          <View
            style={[styles.iconContainer, { backgroundColor: colors.border }]}
          >
            {renderedIcon}
          </View>
          <View style={[styles.titleTextContainer, titleTextContainerStyle]}>
            <ThemedText
              style={[fonts.regular, { color: colors.border }]}
              type="subtitle"
            >
              {subtitle}
            </ThemedText>
            <ThemedText style={fonts.semiBold} type="title">
              {title}
            </ThemedText>
            {leftButton && rightButton && (
              <View style={styles.buttonContainer}>
                {leftButton}
                {rightButton}
              </View>
            )}
          </View>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: "#fff",
    borderTopWidth: 1,
    borderTopColor: "#e0e0e0",
    paddingVertical: 32,
    paddingHorizontal: 40,
    width: "100%",
  },
  content: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: 8,
  },
  iconContainer: {
    width: 58,
    height: 58,
    borderRadius: 29,
    alignItems: "center",
    justifyContent: "center",
  },
  titleContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 16,
  },
  titleTextContainer: {
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    gap: 4,
  },
  buttonContainer: {
    position: "absolute",
    right: 0,
    bottom: 0,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 8,
    padding: 8,
  },
});
